// @ts-strict-ignore
import { Dialog, Flex, HubbleProvider, IconBadge, Text } from "@hubble/web";
import type { ReactNode } from "react";
import React, { useEffect, useState } from "react";
import { IconBrandX } from "@hubble/icons";
import {
  FacebookShareButton,
  LinkedinShareButton,
  RedditShareButton,
  TelegramShareButton,
  TwitterShareButton
} from "react-share";
import {
  FacebookIconStatic,
  SocialShareButtonsContainer,
  SocialShareModalContent,
  SocialShareModalIconsWrapper
} from "./style";
import linkedinIconBrand from "components/parts/social/linkedin-icon-brand";
import RedditIconBrand from "components/parts/social/reddit-icon-brand";
import TelegramIconBrand from "components/parts/social/telegram-icon-brand";
import { mixpanelClient } from "analytics/mixpanel";
import { EVENTS } from "analytics/mixpanel/events";
import queryStringPkg from "query-string";
import { generateBase64HashForCoin } from "utils/prices";

const SocialShareModal = ({
  showModal,
  setShowModal,
  children,
  title,
  OGImageAPIData,
  disableCTAs = true,
  coin
}: {
  showModal: boolean;
  setShowModal: (boolean) => void;
  children?: ReactNode;
  title?: string | ReactNode;
  OGImageAPIData?: Record<string, unknown> | null;
  disableCTAs?: boolean;
  coin: string;
}) => {
  const { cs, pc, pr, ts, cn } = OGImageAPIData ?? {};
  const [windowLocation, setWindowLocation] = useState("");
  useEffect(() => {
    const { origin, search } = window.location;

    const encryptedDataHash = generateBase64HashForCoin({
      coinName: cn as string,
      coinPrice: pr as string,
      coinSymbol: cs as string,
      percentageChange: pc as string,
      timestamp: ts as string
    });
    setWindowLocation(
      `${origin}/prices/${coin || ""}${encryptedDataHash ? `/${encryptedDataHash}` : ""}${search ?? ""}`
    );
  }, [windowLocation, coin, cs, pc, pr, ts, cn]);

  const handleSocialButtonBeforeClick = (handleName: string) => {
    const { search } = window.location;
    mixpanelClient.track(EVENTS.SOCIAL_SHARE_PLATFORM_ICON_CLICK.name, {
      [EVENTS.SOCIAL_SHARE_PLATFORM_ICON_CLICK.properties.COIN_SYMBOL]: cs,
      [EVENTS.SOCIAL_SHARE_PLATFORM_ICON_CLICK.properties.COIN_PRICE]:
        parseFloat(pr as string),
      [EVENTS.SOCIAL_SHARE_PLATFORM_ICON_CLICK.properties.PERCENTAGE_CHANGE]:
        parseFloat(pc as string),
      [EVENTS.SOCIAL_SHARE_PLATFORM_ICON_CLICK.properties.SOCIAL_PLATFORM_NAME]:
        handleName,
      ...queryStringPkg.parse(search)
    });
  };

  return (
    <HubbleProvider scheme="light">
      <Dialog
        open={showModal}
        onOpenChange={(open) => {
          if (!open) setShowModal(false);
        }}
        aria-label="social-share-modal"
        modal
      >
        <Dialog.Container>
          <Dialog.Header>
            <Dialog.Title align="left">{title}</Dialog.Title>
          </Dialog.Header>
          <Dialog.Content
            style={{
              padding: "1rem"
            }}
          >
            <SocialShareModalContent
              marginTop={"1.5rem"}
              alignItems="center"
              justifyContent="center"
              maxWidth={"100%"}
            >
              {children}
            </SocialShareModalContent>

            <SocialShareModalIconsWrapper>
              <SocialShareButtonsContainer
                gap={"2.5rem"}
                marginTop={"2rem"}
                maxWidth={"100%"}
                overflow="auto"
              >
                {[
                  {
                    title: "",
                    handleName: "facebook web",
                    ShareButton: FacebookShareButton,
                    Icon: FacebookIconStatic,
                    textLabel: "Facebook",
                    badgeStyle: {
                      backgroundColor: "#1877F2",
                      color: "white"
                    },
                    iconStyle: { color: "white", backgroundColor: "#fff" }
                  },
                  {
                    title: "",
                    handleName: "twitter web",
                    ShareButton: TwitterShareButton,
                    Icon: IconBrandX,
                    textLabel: "X",
                    badgeStyle: {
                      color: "#fff",
                      backgroundColor: "#000"
                    }
                  },
                  {
                    title: "",
                    handleName: "telegram web",
                    ShareButton: TelegramShareButton,
                    Icon: TelegramIconBrand,
                    textLabel: "Telegram"
                  },
                  {
                    title: "",
                    handleName: "reddit web",
                    ShareButton: RedditShareButton,
                    Icon: RedditIconBrand,
                    textLabel: "Reddit",
                    badgeStyle: {
                      backgroundColor: "#FF4500"
                    }
                  },
                  {
                    title: "",
                    handleName: "linkedin web",
                    ShareButton: LinkedinShareButton,
                    Icon: linkedinIconBrand,
                    textLabel: "Linkedin",
                    badgeStyle: {
                      backgroundColor: "#0b66c2"
                    }
                  }
                ].map(
                  ({
                    title,
                    ShareButton,
                    Icon,
                    textLabel,
                    badgeStyle = {},
                    iconStyle = {},
                    handleName
                  }) => {
                    return (
                      <ShareButton
                        beforeOnClick={() =>
                          handleSocialButtonBeforeClick(handleName)
                        }
                        key="title"
                        url={windowLocation}
                        title={title}
                        disabled={disableCTAs}
                        style={{
                          cursor: disableCTAs ? "not-allowed" : "pointer"
                        }}
                      >
                        <Flex
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                          gap={"8px"}
                        >
                          <IconBadge
                            {...badgeStyle}
                            size="xl"
                            icon={<Icon size="xl" {...iconStyle} />}
                          />
                          <Text.Body size="sm">{textLabel}</Text.Body>
                        </Flex>
                      </ShareButton>
                    );
                  }
                )}
              </SocialShareButtonsContainer>
            </SocialShareModalIconsWrapper>
          </Dialog.Content>
        </Dialog.Container>
      </Dialog>
    </HubbleProvider>
  );
};

export default SocialShareModal;
